// Linking a job to its bid.
//
// Why this exists: a still-SYSTAC placeholder is edit-locked, so a PM who already has
// the real Sage number cannot rename it, and creates the job by hand instead. That
// leaves TWO projects for one job: the bid's placeholder, which the bid keeps feeding
// and the crew schedule keeps counting, and the real one, which the bid knows nothing
// about. Job numbers are unique, so when the bid is won the placeholder can never be
// renamed to the number the real job already holds.
//
// Two places use this:
//   - the New Project form lists the open bid placeholders that look like the job being
//     typed, and makes the PM say whether it is one of them (`useBidMatches`,
//     `BidMatchPanel`), so the duplicate is never created;
//   - the Overview of a real job that has no bid offers "Link to a bid" (`BidLinkSection`,
//     in BidLinkSection.tsx), for the ones that already exist.
//
// The API does the work in one transaction (POST /projects/:id/link-bid): the job takes
// the bid, and the placeholder is archived, not deleted.
//
// ⚠️ THIS FILE IS IMPORTED BY THE PROJECTS LIST (the New Project form), so it must not use
// a class declared on the workspace route (`pw-card`, `ov-*`): the co-presence guard
// (styles/cssCoPresence.spec.ts) fails it, and in the browser the class would simply not be
// there. The Overview card, which does use them, is in BidLinkSection.tsx.

import { useEffect, useState } from 'react';
import { useQueries } from '@tanstack/react-query';
import { projectsApi, type BidPlaceholder } from '@/lib/apiClient';

// ─── Pure helpers (tested directly) ─────────────────────────────────────────

/** How far along the bid is, in the words a PM uses. */
export function bidStageLabel(stage: BidPlaceholder['stage']): string {
  return stage === 'won' ? 'Bid won, waiting for a Sage job number' : 'Bid still in progress';
}

/** What the PM said about whether the new job is one of the listed bids. */
export type BidChoice = number | 'none' | null;

/**
 * The choice that actually applies. With nothing listed there is nothing to decide, so
 * it is `'none'`. With matches listed it must be an explicit pick (`null` until made),
 * and a pick that is no longer listed (the PM kept typing and the match went away) does
 * not count, so a stale choice can never link a job to a bid it no longer resembles.
 *
 * `answeredAgainst` is the ids that were listed when the PM answered. "No, a different
 * job" is an answer about THOSE bids: if the PM keeps typing and a bid they were never
 * shown appears, it does not carry over, and they are asked again.
 */
export function resolveBidChoice(
  matches: BidPlaceholder[],
  choice: BidChoice,
  answeredAgainst?: number[],
): BidChoice {
  if (matches.length === 0) return 'none';
  if (choice === 'none') {
    return !answeredAgainst || matches.every((m) => answeredAgainst.includes(m.id)) ? 'none' : null;
  }
  if (typeof choice === 'number' && matches.some((m) => m.id === choice)) return choice;
  return null;
}

/**
 * Create the job, then link it if the PM picked a bid. Two requests, so the second can
 * fail with the first already done: the job then exists and is NOT linked, which is
 * reported (`linkError`) rather than thrown, because throwing would tell the PM their
 * job was not created and invite a second one. They can link it from the job's page.
 */
export async function createThenLink<T extends { id: number }>(
  create: () => Promise<T>,
  choice: BidChoice,
  link: (projectId: number, placeholderProjectId: number) => Promise<unknown>,
): Promise<{ project: T; linkError: string | null }> {
  const project = await create();
  if (typeof choice !== 'number') return { project, linkError: null };
  try {
    await link(project.id, choice);
    return { project, linkError: null };
  } catch (e) {
    return { project, linkError: (e as { message?: string } | null)?.message ?? 'The link failed.' };
  }
}

// ─── Data hooks ─────────────────────────────────────────────────────────────

export function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

const MIN_CHARS = 3;

/**
 * Open bid placeholders that look like the job being created: those with ANY word of the
 * typed title or client in their own title, client or number, best match first. Any word,
 * not every word, because a missed duplicate costs a second project while a needless
 * suggestion costs one click ("Acme Warehouse Phase 2" must still find "Acme Warehouse").
 * Asked separately for the title and the client (the placeholder's name is the bid's, which
 * a PM may have worded differently) and merged, title matches first. Nothing is asked until
 * three characters are in.
 *
 * `isPending` is true from the keystroke until the answer for what is NOW typed has come
 * back (the debounce, then the request). The form holds its submit button for that
 * window: otherwise a PM who types a title and clicks Create at once gets "no matches"
 * for the half-second the answer has not arrived, and the duplicate this exists to stop.
 * A failed request settles it: the form must not be stuck behind an outage.
 */
export function useBidMatches(title: string, client: string): { matches: BidPlaceholder[]; isPending: boolean } {
  const t = useDebounced(title.trim(), 300);
  const c = useDebounced(client.trim(), 300);
  const terms = [t, c].map((x) => (x.length >= MIN_CHARS ? x : ''));
  const results = useQueries({
    queries: terms.map((q) => ({
      // ⚠️ 'any' IS PART OF THE KEY, and that is what keeps a term that is not asked (too
      // short, so '') from reading the wrong answer: a disabled query returns whatever is
      // cached under its key, and the Overview picker files its UNFILTERED list under
      // ['bid-placeholders', '']. Without the 'any' this blank term would share that key and
      // list every open bid as a match for an empty form. It also keeps the same words asked
      // the picker's way (every word) from being served as this duplicate check's answer.
      queryKey: ['bid-placeholders', 'any', q],
      queryFn: () => projectsApi.bidPlaceholders(q, 'any'),
      enabled: q !== '',
      staleTime: 15_000,
    })),
  });
  const seen = new Set<number>();
  const matches: BidPlaceholder[] = [];
  for (const r of results) {
    for (const m of r.data ?? []) {
      if (!seen.has(m.id)) { seen.add(m.id); matches.push(m); }
    }
  }
  const isPending = t !== title.trim() || c !== client.trim() || results.some((r) => r.isFetching);
  return { matches, isPending };
}

// ─── Shared list ────────────────────────────────────────────────────────────

const rowStyle: React.CSSProperties = {
  display: 'flex', gap: 10, alignItems: 'flex-start', padding: '8px 10px',
  border: '1px solid var(--border)', borderRadius: 'var(--ew-r-md)', cursor: 'pointer', background: '#fff',
};

export function PlaceholderRows({ items, name, value, onChange, grouped = true }: {
  items: BidPlaceholder[];
  name: string;
  value: number | null;
  onChange: (id: number) => void;
  /** False when the caller wraps these in a radiogroup of its own (the form adds one more choice). */
  grouped?: boolean;
}) {
  return (
    <div {...(grouped ? { role: 'radiogroup', 'aria-label': 'Open bids' } : {})} style={{ display: 'grid', gap: 6 }}>
      {items.map((m) => (
        <label key={m.id} style={{ ...rowStyle, borderColor: value === m.id ? 'var(--ew-green)' : 'var(--border)' }}>
          <input type="radio" name={name} checked={value === m.id} onChange={() => onChange(m.id)} style={{ marginTop: 3 }} />
          <span style={{ display: 'grid', gap: 2, minWidth: 0 }}>
            <span style={{ fontSize: 13, fontWeight: 600, overflowWrap: 'anywhere' }}>{m.jobNumber} — {m.title}</span>
            <span style={{ fontSize: 12, color: 'var(--gray)' }}>
              {m.client ? `${m.client} · ` : ''}{bidStageLabel(m.stage)}
            </span>
          </span>
        </label>
      ))}
    </div>
  );
}

// ─── New Project form: "is this job already a bid?" ─────────────────────────

/**
 * Shown by the New Project form when open bid placeholders look like the job being
 * typed. The PM must answer: pick the bid, or say it is a different job. Without an
 * answer the form cannot be submitted, because the quiet default is the duplicate.
 */
export function BidMatchPanel({ matches, choice, onChoose }: {
  matches: BidPlaceholder[];
  choice: BidChoice;
  onChoose: (c: BidChoice) => void;
}) {
  if (matches.length === 0) return null;
  return (
    <div style={{ marginTop: 14, padding: 12, border: '1px solid #E6D49A', background: '#FFF8E1', borderRadius: 'var(--ew-r-md)' }}>
      <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>Is this job already in Bid Tracker?</div>
      <div style={{ fontSize: 12, color: 'var(--gray)', marginBottom: 8 }}>
        These bids have a placeholder here with no job number yet. If one is this job, pick it: the new job takes over
        the bid and the placeholder is archived. If you skip this, the job and the placeholder both stay, and the bid never learns this job number.
      </div>
      {/* One question, one group: the bids AND "different job" are its choices. */}
      <div role="radiogroup" aria-label="Is this job already in Bid Tracker?">
        <PlaceholderRows
          items={matches}
          name="bid-match"
          value={typeof choice === 'number' ? choice : null}
          onChange={(id) => onChoose(id)}
          grouped={false}
        />
        <label style={{ ...rowStyle, marginTop: 6, borderColor: choice === 'none' ? 'var(--ew-green)' : 'var(--border)' }}>
          <input type="radio" name="bid-match" checked={choice === 'none'} onChange={() => onChoose('none')} style={{ marginTop: 3 }} />
          <span style={{ fontSize: 13 }}>No, this is a different job</span>
        </label>
      </div>
      {choice === null && (
        <div style={{ fontSize: 12, color: '#8F2414', marginTop: 6 }}>Choose one to continue.</div>
      )}
    </div>
  );
}
